/**
 * タイトルロゴの「打って完成させる」ロジック(docs/15_game_feel.md §1-1)。
 *
 * Typing Land はタイトルロゴ自体が遊びになっていて、`TYPINGLAND` と打つと
 * 1文字ずつタイルに色が付く。PaLTown では `PALTOWN` の7タイルで同じことをする。
 *
 * ここは**描画非依存の純ロジック**。React も DOM も知らない(単体テストしやすくするため)。
 *
 * 遊びなので「強制しない・叱らない」:
 * - 打ち間違えても音を出す以上のペナルティは無い。ただ最初に戻るだけ
 * - 間違えた文字がたまたま `P` なら、そのまま1文字目として数え直す(打ち直しがすぐ効く)
 * - 完成後にまた `P` を打てば頭から遊び直せる
 */

/** タイルに並べる文字列。変えるときは `.logo-tile.c0`〜 の色クラスも合わせる。 */
export const LOGO_WORD = 'PALTOWN';

/** 1入力の結果。演出(ポップ・SE・星バースト)の出し分けに使う。 */
export type LogoTypingHit =
  /** 次の1文字が当たった。タイルが色づいてポップする。 */
  | 'advance'
  /** 別の文字だったが `P` だったので1文字目としてやり直し。 */
  | 'restart'
  /** 別の文字だったので最初に戻る。 */
  | 'reset'
  /** ロゴ遊びとは関係ない入力(修飾キー・数字・記号など)。状態は変えない。 */
  | 'ignore';

export interface LogoTypingResult {
  /** 更新後の「そろっている文字数」。0〜`LOGO_WORD.length`。 */
  readonly typed: number;
  readonly hit: LogoTypingHit;
  /** この入力でちょうど全部そろったか(星バーストのトリガ)。 */
  readonly completed: boolean;
}

/** アルファベット1文字の入力か。`KeyboardEvent.key` をそのまま渡してよい。 */
function letterOf(key: string): string | null {
  if (key.length !== 1) return null;
  const upper = key.toUpperCase();
  return upper >= 'A' && upper <= 'Z' ? upper : null;
}

/** そろい切っているか。 */
export function isLogoComplete(typed: number): boolean {
  return typed >= LOGO_WORD.length;
}

/**
 * いま `typed` 文字そろっている状態で `key` を打ったときの次の状態。
 *
 * 完成済みの状態から打つと、その1打を「1文字目」として数え直す(遊び直し)。
 */
export function advanceLogoTyping(typed: number, key: string): LogoTypingResult {
  const letter = letterOf(key);
  if (letter === null) return { typed, hit: 'ignore', completed: false };

  // 完成後の1打は頭から数え直す。
  const base = isLogoComplete(typed) ? 0 : Math.max(0, typed);

  if (letter === LOGO_WORD[base]) {
    const next = base + 1;
    return { typed: next, hit: 'advance', completed: isLogoComplete(next) };
  }
  if (letter === LOGO_WORD[0]) return { typed: 1, hit: 'restart', completed: false };
  return { typed: 0, hit: 'reset', completed: false };
}
